// public/js/modules/map/core.js
import { setMapInstance, markers, mapInstance, currentFilteredAssets } from './state.js';
import { clearMarkers } from './markers.js';
import { DEFAULT_MAPLIBRE_STYLE, BASE_LAYERS, GLYPHS_URL, isValidLatLon } from './constants.js';

const BASE_LAYER_STORAGE_KEY = 'map.baseLayerId';

// Build a minimal MapLibre style object for a raster (XYZ) base layer.
// We attach GLYPHS_URL so our own symbol layers (cluster counts / labels /
// arrows) still find their fonts on top of the raster tiles.
function buildRasterStyle(def) {
  return {
    version: 8,
    glyphs: GLYPHS_URL,
    sources: {
      'raster-base': {
        type: 'raster',
        tiles: def.tiles,
        tileSize: def.tileSize || 256,
        maxzoom: def.maxzoom || 19,
        attribution: def.attribution || ''
      }
    },
    layers: [{ id: 'raster-base-layer', type: 'raster', source: 'raster-base' }]
  };
}

export function getStoredBaseLayerId() {
  try { return localStorage.getItem(BASE_LAYER_STORAGE_KEY); } catch (e) { return null; }
}

/**
 * Switch the base map. setStyle() keeps the current camera but wipes all custom
 * sources/layers, so the caller passes onReady() to re-add asset/cluster/history
 * data once the new style has loaded. Map-level click handlers survive setStyle,
 * so cluster/marker interaction keeps working after a switch.
 */
export function setBaseStyle(map, layerId, onReady) {
  if (!map) return;
  const def = BASE_LAYERS.find(l => l.id === layerId) || BASE_LAYERS[0];

  try { localStorage.setItem(BASE_LAYER_STORAGE_KEY, def.id); } catch (e) {}

  const style = def.type === 'raster' ? buildRasterStyle(def) : def.style;
  map.__styleReady = false;   // new style loading — block renders until it's ready again

  // Fire onReady exactly once. 'style.load' can fire SYNCHRONOUSLY inside
  // setStyle for object styles (our raster layers), so the listener must be
  // registered BEFORE setStyle — registering after silently missed it, which
  // is why routes/POIs only came back on SOME base layers. A poll on
  // isStyleLoaded() backs up any path where the event does not fire at all.
  let done = false;
  let poll = null;
  const ready = () => {
    if (done) return;
    done = true;
    if (poll) { clearInterval(poll); poll = null; }
    map.__styleReady = true;
    try { onReady?.(); } catch (e) { console.warn('[map] base style onReady failed', e); }
  };
  map.once('style.load', ready);

  // diff:false forces a full style load, so the ready signal is deterministic —
  // the diff path applies changes without ever firing 'style.load'.
  map.setStyle(style, { diff: false });

  poll = setInterval(() => { if (map.isStyleLoaded()) ready(); }, 100);
  setTimeout(() => { if (poll) { clearInterval(poll); poll = null; } if (!done) ready(); }, 5000);
  console.log('[map] Base layer switched →', def.id);
}

// Resolve the initial style: 1) the user's last picked base layer, 2) an
// APP_CONFIG override (e.g. a MapTiler/PMTiles URL injected server-side),
// 3) the OpenFreeMap default. Returns a style URL string OR a style object
// (for raster base layers).
export function getInitialStyle() {
  const storedId = getStoredBaseLayerId();
  if (storedId) {
    const def = BASE_LAYERS.find(l => l.id === storedId);
    if (def) return def.type === 'raster' ? buildRasterStyle(def) : def.style;
  }

  const cfg = window.APP_CONFIG?.MAPLIBRE_STYLE_URL;
  if (cfg && typeof cfg === 'string' && cfg.trim().length > 5) {
    return cfg.trim();
  }
  return DEFAULT_MAPLIBRE_STYLE;
}

export async function initMap(containerId = 'map') {
  const mapDiv = document.getElementById(containerId);
  if (!mapDiv) return null;

  // Wait for maplibregl to be available (CDN load)
  let attempts = 0;
  while (typeof maplibregl === 'undefined' && attempts < 120) {
    await new Promise(r => setTimeout(r, 50));
    attempts++;
  }

  if (typeof maplibregl === 'undefined') {
    mapDiv.innerHTML = `
      <div class="d-flex align-items-center justify-content-center h-100 bg-dark text-white">
        <div class="text-center">
          <h5>MapLibre GL JS not available</h5>
          <p class="small text-white-50">Please reload the page or check your network.</p>
          <button onclick="location.reload()" class="btn btn-outline-light btn-sm mt-2">Reload</button>
        </div>
      </div>`;
    return null;
  }

  const initialStyle = getInitialStyle();
  console.log('[map] Using MapLibre style:', initialStyle);

  if (typeof initialStyle === 'string' && initialStyle.includes('demotiles')) {
    console.log('%c[map] Tip: demotiles is just the world demo without detail. For a real map, set OpenFreeMap (default) or a MapTiler/PMTiles style URL in APP_CONFIG.MAPLIBRE_STYLE_URL (see constants.js)', 'color:#f59e0b');
  }

  const map = new maplibregl.Map({
    container: containerId,
    style: initialStyle,
    center: [8.2, 46.8],   // [lng, lat] !!!
    zoom: 7,
    attributionControl: true
  });

  // Add some standard controls (right side)
  map.addControl(new maplibregl.NavigationControl({ showCompass: true }), 'top-right');
  map.addControl(new maplibregl.ScaleControl({ unit: 'metric' }), 'bottom-left');

  setMapInstance(map);

  // Track style readiness ourselves. MapLibre's isStyleLoaded() returns false
  // for a moment even AFTER 'style.load' has fired, which made every render wait
  // on the safety timeout (a 3s hang per render). This flag is the truth: once
  // 'style.load'/'load' fires, sources/layers can be added. Fires again on
  // base-layer switches too (see setBaseStyle). See rendering.js / POI layers.
  map.__styleReady = false;
  map.on('style.load', () => { map.__styleReady = true; });

  // When the map first loads, we may want to resize if container was hidden
  map.once('load', () => {
    map.__styleReady = true;
    map.resize();
    console.log('✅ MapLibre map (beta) loaded');
  });

  return map;
}

export function getMapInstance() {
  return mapInstance;
}

export function centerOnAsset(assetUUID, map) {
  if (!map) return;
  const marker = markers.get(assetUUID);
  if (marker) {
    const lngLat = marker.getLngLat();
    map.flyTo({ center: lngLat, zoom: Math.max(map.getZoom(), 13), duration: 600 });
  }
}

export function fitAllMarkers(map) {
  if (!map) return;

  const bounds = new maplibregl.LngLatBounds();
  let hasAny = false;

  // Primary source: the filtered assets. This works for BOTH render paths —
  // clustering (GeoJSON layer, no DOM markers) and individual DOM markers.
  (currentFilteredAssets || []).forEach(asset => {
    if (isValidLatLon(asset.lastLat, asset.lastLon)) {
      bounds.extend([parseFloat(asset.lastLon), parseFloat(asset.lastLat)]);
      hasAny = true;
    }
  });

  // Fallback: DOM markers (e.g. if called before assets state is populated).
  if (!hasAny) {
    markers.forEach(m => {
      const ll = m.getLngLat();
      if (ll) {
        bounds.extend(ll);
        hasAny = true;
      }
    });
  }

  if (hasAny) {
    map.fitBounds(bounds, { padding: 60, maxZoom: 15, duration: 800 });
  } else {
    window.showToast?.('No assets with a position to fit', 'info');
  }
}

export function clearAllMapData() {
  console.log('[Map] clearAllMapData called');

  const map = mapInstance;
  if (!map) return;

  try {
    // History: layers first, then sources. Use all known current IDs.
    const historyLayers = [
      'history-route-base-layer',
      'history-route-recent-layer',
      'history-route-arrows-layer',
      'asset-accuracy-line',
      'asset-accuracy-fill',
      'asset-accuracy-tube-fill'
    ];
    historyLayers.forEach(id => {
      try { if (map.getLayer(id)) map.removeLayer(id); } catch (e) {}
    });

    const historySources = [
      'history-route-base-src',
      'history-route-recent-src',
      'history-route-arrows-src',
      'asset-accuracy-src',
      'asset-accuracy-tube-src'
    ];
    historySources.forEach(id => {
      try { if (map.getSource(id)) map.removeSource(id); } catch (e) {}
    });
  } catch (e) {}

  try {
    // Clusters: layers first, then source. Include the labels layer.
    ['clusters', 'cluster-count', 'unclustered-points', 'unclustered-labels'].forEach(id => {
      try { if (map.getLayer(id)) map.removeLayer(id); } catch (e) {}
    });
    try { if (map.getSource('asset-clusters')) map.removeSource('asset-clusters'); } catch (e) {}
  } catch (e) {}

  // Clear individual markers
  try { clearMarkers(); } catch (e) {}
}


// Called from router when leaving #map
export function resizeMapIfNeeded(map) {
  if (map && typeof map.resize === 'function') {
    // small delay so the container is visible
    setTimeout(() => {
      try { map.resize(); } catch (e) {}
    }, 60);
  }
}
